import round from 'lodash/round';
import { defineComponent } from 'vue-demi';
import { openBlock, createElementBlock, normalizeClass, Fragment, renderSlot, createElementVNode, createTextVNode, toDisplayString, createCommentVNode, normalizeStyle } from 'vue';
import { s as styleInject } from './style-inject.es-4c6f2515.js';

var OrProgressColors;
(function (OrProgressColors) {
  OrProgressColors["auto"] = "auto";
  OrProgressColors["primary"] = "primary";
  OrProgressColors["success"] = "success";
  OrProgressColors["warning"] = "warning";
  OrProgressColors["error"] = "error";
})(OrProgressColors || (OrProgressColors = {}));
var OrProgressTypes;
(function (OrProgressTypes) {
  OrProgressTypes["linear"] = "linear";
  OrProgressTypes["circle"] = "circle";
})(OrProgressTypes || (OrProgressTypes = {}));

var script = defineComponent({
  name: 'OrProgress',
  props: {
    maxValue: {
      type: Number,
      required: false,
      default: 1
    },
    value: {
      type: Number,
      required: false,
      default: null
    },
    progress: {
      type: Number,
      required: false,
      default: null
    },
    precision: {
      type: Number,
      required: false,
      default: 2
    },
    type: {
      type: String,
      required: false,
      default: OrProgressTypes.linear
    },
    color: {
      type: String,
      required: false,
      default: OrProgressColors.auto
    },
    headerText: {
      type: String,
      required: false,
      default: null
    },
    footerText: {
      type: String,
      required: false,
      default: null
    },
    isLoading: {
      type: Boolean,
      required: false,
      default: false
    }
  },
  computed: {
    localProgress() {
      var _a, _b;
      return (_a = this.progress) !== null && _a !== void 0 ? _a : ((_b = this.value) !== null && _b !== void 0 ? _b : 0) / this.maxValue;
    },
    localValue() {
      var _a, _b;
      return (_a = this.value) !== null && _a !== void 0 ? _a : ((_b = this.progress) !== null && _b !== void 0 ? _b : 0) * this.maxValue;
    },
    percent() {
      return round(this.localProgress * 100, this.precision) + '%';
    },
    isLinearType() {
      return this.type === OrProgressTypes.linear;
    },
    isCircleType() {
      return this.type === OrProgressTypes.circle;
    },
    isShowingHeader() {
      var _a;
      return !!this.headerText || !!this.$slots.header || !!this.$slots.label || !!this.$slots.percent || !!((_a = this.$scopedSlots) === null || _a === void 0 ? void 0 : _a.percent);
    },
    isShowingFooter() {
      var _a;
      return !!this.footerText || !!this.$slots.footer || !!((_a = this.$scopedSlots) === null || _a === void 0 ? void 0 : _a.footer);
    },
    localColor() {
      if (this.color === OrProgressColors.auto) {
        if (this.localProgress <= 0.5) {
          return OrProgressColors.success;
        } else if (this.localProgress <= 0.95) {
          return OrProgressColors.warning;
        } else {
          return OrProgressColors.error;
        }
      } else {
        return Object.values(OrProgressColors).includes(this.color) ? this.color : OrProgressColors.success;
      }
    },
    isMoreThanHalf() {
      return this.localProgress > 0.5;
    },
    progressBarClasses() {
      const colorClass = `is-${this.localColor}-color`;
      return ['or-progress-bar', colorClass];
    },
    progressPieClasses() {
      const colorClass = `is-${this.localColor}-color`;
      return ['or-progress-pie', colorClass, ...(this.isLoading ? ['is-loading'] : [])];
    },
    progressBarStyle() {
      return {
        transform: `scaleX(${this.localProgress})`
      };
    },
    leftCircleHalfStyle() {
      const angle = this.isMoreThanHalf ? this.localProgress * 360 : 180;
      return {
        transform: `rotate(${angle}deg)`,
        visibility: this.isMoreThanHalf ? 'visible' : 'hidden'
      };
    },
    rightCircleHalfStyle() {
      const angle = this.isMoreThanHalf ? 180 : this.localProgress * 360;
      return {
        transform: `rotate(${angle}deg)`
      };
    },
    pieCircleStyle() {
      return this.localProgress <= 0.5 ? {} : {
        clip: 'rect(auto, auto, auto, auto)'
      };
    }
  }
});

const _hoisted_1 = {
  key: 0,
  class: "or-progress-header"
};
const _hoisted_2 = {
  class: "label"
};
const _hoisted_3 = {
  class: "percent"
};
const _hoisted_4 = {
  class: "or-progress-linear"
};
const _hoisted_5 = {
  key: 0,
  class: "or-progress-loader"
};
const _hoisted_6 = {
  key: 1,
  class: "or-progress-footer"
};
const _hoisted_7 = {
  key: 1,
  class: "or-progress-circle"
};
function render(_ctx, _cache, $props, $setup, $data, $options) {
  return openBlock(), createElementBlock("div", {
    class: normalizeClass(["or-progress", {
      'is-linear': _ctx.isLinearType
    }])
  }, [_ctx.isLinearType ? (openBlock(), createElementBlock(Fragment, {
    key: 0
  }, [_ctx.isShowingHeader ? (openBlock(), createElementBlock("div", _hoisted_1, [renderSlot(_ctx.$slots, "header", {}, () => [createElementVNode("div", _hoisted_2, [renderSlot(_ctx.$slots, "label", {}, () => [createTextVNode(toDisplayString(_ctx.headerText), 1 /* TEXT */)])]), createElementVNode("div", _hoisted_3, [renderSlot(_ctx.$slots, "percent", {
    value: _ctx.percent
  }, () => [createTextVNode(toDisplayString(_ctx.percent), 1 /* TEXT */)])])])])) : createCommentVNode("v-if", true), createElementVNode("div", _hoisted_4, [_ctx.isLoading ? (openBlock(), createElementBlock("div", _hoisted_5)) : (openBlock(), createElementBlock("div", {
    key: 1,
    class: normalizeClass(_ctx.progressBarClasses),
    style: normalizeStyle(_ctx.progressBarStyle)
  }, null, 6 /* CLASS, STYLE */))]), _ctx.isShowingFooter ? (openBlock(), createElementBlock("div", _hoisted_6, [renderSlot(_ctx.$slots, "footer", {
    value: _ctx.localValue
  }, () => [createTextVNode(toDisplayString(_ctx.footerText), 1 /* TEXT */)])])) : createCommentVNode("v-if", true)], 64 /* STABLE_FRAGMENT */)) : _ctx.isCircleType ? (openBlock(), createElementBlock("div", _hoisted_7, [createElementVNode("div", {
    class: normalizeClass(_ctx.progressPieClasses),
    style: normalizeStyle(_ctx.pieCircleStyle)
  }, [createElementVNode("div", {
    class: "left-side half-circle",
    style: normalizeStyle(_ctx.leftCircleHalfStyle)
  }, null, 4 /* STYLE */), createElementVNode("div", {
    class: "right-side half-circle",
    style: normalizeStyle(_ctx.rightCircleHalfStyle)
  }, null, 4 /* STYLE */)], 6 /* CLASS, STYLE */)])) : createCommentVNode("v-if", true)], 2 /* CLASS */);
}

var css_248z = "/**\n * A simple way to manage mobile breakpoints described in https://davidwalsh.name/write-media-queries-sass.\n * Can be further extended using following approach https://rimdev.io/making-media-query-mixins-with-sass/\n */\n@keyframes spinAround-cb4dbcd0 {\nfrom {\n    transform: rotate(0);\n}\nto {\n    transform: rotate(359deg);\n}\n}\n@keyframes fade-cb4dbcd0 {\nfrom {\n    opacity: 1;\n}\nto {\n    opacity: 0;\n}\n}\n@keyframes loaderLinear-cb4dbcd0 {\n0% {\n    transform: translate(-100%, 0);\n}\n100% {\n    transform: translate(100%, 0);\n}\n}\n.or-progress[data-v-cb4dbcd0] {\n  font-size: var(--fs-0);\n  line-height: var(--lh-0);\n  color: var(--c-neutral-5);\n}\n.or-progress.is-linear[data-v-cb4dbcd0] {\n  display: flex;\n  flex-direction: column;\n  width: 100%;\n  min-width: 10rem;\n  max-width: 18.4375rem;\n}\n.or-progress .or-progress-header[data-v-cb4dbcd0] {\n  display: flex;\n  flex-direction: row;\n  margin-bottom: 0.375rem;\n}\n.or-progress .or-progress-header .label[data-v-cb4dbcd0] {\n  font-weight: bold;\n  color: var(--c-neutral-6);\n}\n.or-progress .or-progress-header .percent[data-v-cb4dbcd0] {\n  margin-left: auto;\n}\n.or-progress .or-progress-linear[data-v-cb4dbcd0] {\n  position: relative;\n  display: block;\n  width: 100%;\n  height: var(--s-1);\n  overflow: hidden;\n  background-color: var(--c-neutral-2);\n  border-radius: 1px;\n  transition-timing-function: ease;\n  transition-duration: 0.3s;\n  transition-property: height, opacity;\n}\n.or-progress .or-progress-linear .or-progress-loader[data-v-cb4dbcd0],\n.or-progress .or-progress-linear .or-progress-loader[data-v-cb4dbcd0]::after,\n.or-progress .or-progress-linear .or-progress-bar[data-v-cb4dbcd0] {\n  position: absolute;\n  top: 0;\n  bottom: 0;\n  left: 0;\n  width: 100%;\n  height: auto;\n}\n.or-progress .or-progress-linear .or-progress-loader[data-v-cb4dbcd0] {\n  animation: loaderLinear-cb4dbcd0 1.2s var(--t-func) infinite;\n}\n.or-progress .or-progress-linear .or-progress-loader[data-v-cb4dbcd0]::after {\n  max-width: 7rem;\n  content: \"\";\n  background-color: var(--c-neutral-5);\n  border-radius: var(--s-1);\n}\n.or-progress .or-progress-linear .or-progress-bar[data-v-cb4dbcd0] {\n  transition: transform 0.2s linear;\n  transform-origin: left;\n}\n.or-progress .or-progress-linear .or-progress-bar.is-primary-color[data-v-cb4dbcd0] {\n  background-color: var(--c-primary);\n}\n.or-progress .or-progress-linear .or-progress-bar.is-success-color[data-v-cb4dbcd0] {\n  background-color: var(--c-success);\n}\n.or-progress .or-progress-linear .or-progress-bar.is-warning-color[data-v-cb4dbcd0] {\n  background-color: var(--c-warning);\n}\n.or-progress .or-progress-linear .or-progress-bar.is-error-color[data-v-cb4dbcd0] {\n  background-color: var(--c-error);\n}\n.or-progress .or-progress-circle[data-v-cb4dbcd0] {\n  position: relative;\n  width: 1.5rem;\n  height: 1.5rem;\n}\n.or-progress .or-progress-circle .or-progress-pie[data-v-cb4dbcd0] {\n  clip: rect(0, 1.5rem, 1.5rem, 0.75rem);\n}\n.or-progress .or-progress-circle .or-progress-pie.is-loading[data-v-cb4dbcd0] {\n  clip: rect(auto, auto, auto, auto);\n  animation: spinAround-cb4dbcd0 0.7s linear infinite;\n}\n.or-progress .or-progress-circle .or-progress-pie.is-loading .half-circle[data-v-cb4dbcd0] {\n  visibility: visible !important;\n  border-color: var(--c-primary) !important;\n}\n.or-progress .or-progress-circle .or-progress-pie.is-loading .half-circle.left-side[data-v-cb4dbcd0] {\n  transform: rotate(270deg) !important;\n}\n.or-progress .or-progress-circle .or-progress-pie.is-loading .half-circle.right-side[data-v-cb4dbcd0] {\n  transform: rotate(180deg) !important;\n}\n.or-progress .or-progress-circle .or-progress-pie[data-v-cb4dbcd0],\n.or-progress .or-progress-circle .or-progress-pie .half-circle[data-v-cb4dbcd0] {\n  position: absolute;\n  top: 0;\n  left: 0;\n  width: 100%;\n  height: 100%;\n}\n.or-progress .or-progress-circle .or-progress-pie .half-circle[data-v-cb4dbcd0] {\n  clip: rect(0, 0.75rem, 1.5rem, 0);\n  border-style: solid;\n  border-width: var(--s-1);\n  border-radius: 50%;\n}\n.or-progress .or-progress-circle .or-progress-pie .half-circle.left-side[data-v-cb4dbcd0] {\n  transition: visibility 0.2s linear 0.2s, transform 0.2s linear 0.2s;\n  transform: rotate(180deg);\n}\n.or-progress .or-progress-circle .or-progress-pie .half-circle.right-side[data-v-cb4dbcd0] {\n  transition: transform 0.2s linear;\n}\n.or-progress .or-progress-circle .or-progress-pie.is-primary-color > .half-circle[data-v-cb4dbcd0] {\n  border-color: var(--c-primary);\n}\n.or-progress .or-progress-circle .or-progress-pie.is-success-color > .half-circle[data-v-cb4dbcd0] {\n  border-color: var(--c-success);\n}\n.or-progress .or-progress-circle .or-progress-pie.is-warning-color > .half-circle[data-v-cb4dbcd0] {\n  border-color: var(--c-warning);\n}\n.or-progress .or-progress-circle .or-progress-pie.is-error-color > .half-circle[data-v-cb4dbcd0] {\n  border-color: var(--c-error);\n}\n.or-progress .or-progress-footer[data-v-cb4dbcd0] {\n  margin-top: var(--s-1);\n}";
styleInject(css_248z);

script.render = render;
script.__scopeId = "data-v-cb4dbcd0";
script.__file = "src/components/or-progress/OrProgress.vue";

export { OrProgressColors as O, OrProgressTypes as a, script as s };
